/**
 * Read a tracker export (CSV or Excel) in the browser, with the `xlsx`
 * dependency the app already ships (loaded only when a file is chosen).
 *
 * RAW cells, on purpose: a CSV cell stays the TEXT it is (no "0012" → 12, no
 * guessed dates), and an .xlsx cell is what the workbook holds — text, a
 * number, a boolean, or a date as its serial number. The API reads each cell
 * against its field (dates, money, vocabularies), so nothing is interpreted
 * twice and the dry run can quote the cell exactly as the file has it.
 *
 * The file never leaves the browser: what is sent is each sheet's headings
 * and its rows as arrays of cells (about the size of the CSV), under the API's
 * limits — which are checked here first, so a too-big file is said before it
 * is sent.
 */

export type Cell = string | number | boolean | null;

export interface ParsedSheet {
  name: string;
  headers: string[];
  rows: Cell[][];
  /** Each row's number in the sheet (the heading row is 1), blank rows skipped. */
  rowNumbers: number[];
  /**
   * The workbook counts days from 1904 (old Mac Excel), not 1900: its date
   * serials are 1,462 days apart from the usual ones, and HQ must be told.
   */
  date1904: boolean;
}

/** The API's limits (tracker-imports.schema.ts). */
export const MAX_IMPORT_ROWS = 2000;
export const MAX_IMPORT_COLUMNS = 100;
/** What the browser is asked to open at all. */
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_FILES = ".csv,.xlsx,.xls";

export class TrackerFileError extends Error {}

const cellOf = (v: unknown): Cell => {
  if (v === null || v === undefined) return null;
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return v;
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v);
};

/** Headings as the mapping shows them: trimmed, never empty, never twice the same. */
export function headingsOf(raw: readonly unknown[]): string[] {
  const seen = new Map<string, number>();
  return raw.map((h, i) => {
    let name = h === null || h === undefined || String(h).trim() === "" ? `Column ${i + 1}` : String(h).trim().slice(0, 200);
    const count = seen.get(name.toLowerCase()) ?? 0;
    seen.set(name.toLowerCase(), count + 1);
    if (count > 0) name = `${name} (${count + 1})`;
    return name;
  });
}

/** One sheet's grid → headings and data rows, with their row numbers. */
export function sheetFromGrid(
  name: string,
  grid: ReadonlyArray<ReadonlyArray<unknown> & { __rowNum__?: number }>,
  date1904 = false,
): ParsedSheet {
  const [first, ...rest] = grid;
  if (!first) return { name, headers: [], rows: [], rowNumbers: [], date1904 };
  const width = Math.max(first.length, ...rest.map((r) => r.length));
  if (width > MAX_IMPORT_COLUMNS) {
    throw new TrackerFileError(`Sheet "${name}" has ${width} columns; HQ imports at most ${MAX_IMPORT_COLUMNS}.`);
  }
  const headers = headingsOf(Array.from({ length: width }, (_, i) => first[i]));
  const rows: Cell[][] = [];
  const rowNumbers: number[] = [];
  const headerRow = typeof first.__rowNum__ === "number" ? first.__rowNum__ : 0;
  rest.forEach((r, i) => {
    const cells = Array.from({ length: width }, (_, c) => cellOf(r[c]));
    if (cells.every((c) => c === null || (typeof c === "string" && c.trim() === ""))) return;
    rows.push(cells);
    rowNumbers.push((typeof r.__rowNum__ === "number" ? r.__rowNum__ : headerRow + 1 + i) + 1);
  });
  if (rows.length > MAX_IMPORT_ROWS) {
    throw new TrackerFileError(
      `Sheet "${name}" has ${rows.length.toLocaleString("en-US")} rows; HQ imports at most ${MAX_IMPORT_ROWS.toLocaleString("en-US")} at a time. Split it into smaller files.`,
    );
  }
  return { name, headers, rows, rowNumbers, date1904 };
}

/** Read a chosen file into its sheets (a CSV is one). */
export async function parseTrackerFile(file: File): Promise<ParsedSheet[]> {
  if (file.size > MAX_FILE_BYTES) throw new TrackerFileError("The file is larger than 10 MB. Export just the tracker's rows.");
  const lower = file.name.toLowerCase();
  const csv = lower.endsWith(".csv");
  if (!csv && !lower.endsWith(".xlsx") && !lower.endsWith(".xls")) {
    throw new TrackerFileError("Choose a .csv, .xlsx or .xls file.");
  }
  const XLSX = await import("xlsx");
  let workbook: import("xlsx").WorkBook;
  try {
    workbook = csv
      ? XLSX.read(await file.text(), { type: "string", raw: true, dense: true })
      : XLSX.read(await file.arrayBuffer(), { type: "array", cellDates: false, cellFormula: false, cellHTML: false, dense: true });
  } catch {
    throw new TrackerFileError("The file could not be read as a spreadsheet.");
  }
  const date1904 = !csv && workbook.Workbook?.WBProps?.date1904 === true;
  const sheets = workbook.SheetNames.map((name) => {
    const ws = workbook.Sheets[name];
    const grid = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: null, blankrows: false });
    return sheetFromGrid(name, grid as never, date1904);
  }).filter((s) => s.headers.length > 0);
  if (sheets.length === 0) throw new TrackerFileError("The file has no rows.");
  return sheets;
}
